import {type Location, useNavigation, useRenderedLocation} from '@github-ui/react-router'
import {useEffect, useState} from 'react'

import {baselineLayout} from '../components/saved-view-options/SavedViewUrlConfiguration'
import type {ViewConfiguration} from '../contexts/views/view-configuration'
import {type Layout, readSavedViewUrlOverrides} from '../utils/saved-view-url-params'

/** The parts of a saved view route payload that say which layout is on screen and which one is saved. */
export interface SavedViewLayoutPayload {
  layout: Layout
  configuration?: ViewConfiguration
}

export interface SavedViewNavigation {
  /** Whether a navigation to a URL other than the one on screen is loading. */
  isNavigating: boolean
  /** The search of a navigation on this view that is loading. */
  pendingSearch: string | undefined
  /**
   * The layout the loading response on this view arrives in, when it is not the layout on screen.
   * Null while another view loads, since its saved layout is unknown here.
   */
  pendingLayout: Layout | null
  /** `pendingLayout` once it has loaded for `LAYOUT_PLACEHOLDER_DELAY_MS`, and null before then. */
  layoutPlaceholder: Layout | null
}

/** Matches the `short` delay on the busy board's spinner. */
export const LAYOUT_PLACEHOLDER_DELAY_MS = 300

function isSameLocation(a: Location, b: Location): boolean {
  return a.pathname === b.pathname && a.search === b.search
}

/**
 * The layout the response in flight will arrive in, when that is not the layout on
 * screen. Null when the layout is not what is changing, which is every other request.
 *
 * Read off the destination the router is already holding, through the same reader the
 * request itself goes through, rather than guessed from what was clicked.
 *
 * Both URLs are read, because the parameter is a diff over the saved layout and an absent
 * one means two different things. Dropped from a URL that carried it, the layout is
 * reverting to the saved one. Absent from both, the server resolves this view the same
 * way it just did, and the layout is not what changed.
 *
 * The server has the final say (github/views#211), so this settles which placeholder is
 * drawn and nothing else. The menu keeps reporting the layout that was served.
 */
function layoutInFlight(destination: Location, current: Location, served: Layout, saved: Layout): Layout | null {
  const requestedOverride = readSavedViewUrlOverrides(new URLSearchParams(destination.search)).layout
  const currentOverride = readSavedViewUrlOverrides(new URLSearchParams(current.search)).layout
  if (requestedOverride === undefined && currentOverride === undefined) return null

  const requested = requestedOverride ?? saved
  return requested === served ? null : requested
}

/**
 * Returns `pendingLayout` once it has been loading for `LAYOUT_PLACEHOLDER_DELAY_MS`, and null
 * before then. A layout that arrives sooner, such as one prefetched on hover, replaces the
 * surface on screen without a placeholder in between.
 *
 * The delay runs once per layout change. Picks made while the layout loads keep the placeholder,
 * and a change that lands or is cancelled resets it for the next one.
 */
export function useLayoutPlaceholder(pendingLayout: Layout | null): Layout | null {
  const [elapsedLayout, setElapsedLayout] = useState<Layout | null>(null)
  if (pendingLayout === null && elapsedLayout !== null) setElapsedLayout(null)
  useEffect(() => {
    if (pendingLayout === null) return
    const timer = setTimeout(() => setElapsedLayout(pendingLayout), LAYOUT_PLACEHOLDER_DELAY_MS)
    return () => clearTimeout(timer)
  }, [pendingLayout])
  return pendingLayout !== null && elapsedLayout === pendingLayout ? pendingLayout : null
}

/**
 * The location on screen, and where a loading navigation goes when that is a different URL.
 *
 * Compared against `useRenderedLocation`, since TanStack Router reports the pending location from
 * `useLocation` for a navigation that keeps the pathname.
 */
export function useLoadingNavigation(): {current: Location; destination: Location | null} {
  const navigation = useNavigation()
  const current = useRenderedLocation()
  const destination = navigation.location && !isSameLocation(navigation.location, current) ? navigation.location : null
  return {current, destination}
}

/** Returns the search of a navigation on the view on screen that is loading. */
export function usePendingSavedViewSearch(): string | undefined {
  const {current, destination} = useLoadingNavigation()
  return destination?.pathname === current.pathname ? destination.search : undefined
}

/**
 * Returns what a loading navigation means for the saved view on screen. `payload` is the route
 * response on screen.
 *
 * Needs a data router. A navigation that reloads the URL on screen counts as no navigation. The
 * placeholder delay runs while the caller is mounted, so call this above any branch that hides
 * the surface, such as an edit form.
 */
export function useSavedViewNavigation(payload: SavedViewLayoutPayload): SavedViewNavigation {
  const {current, destination} = useLoadingNavigation()
  const onView = destination?.pathname === current.pathname ? destination : null
  const pendingLayout =
    onView === null ? null : layoutInFlight(onView, current, payload.layout, baselineLayout(payload.configuration))
  return {
    isNavigating: destination !== null,
    pendingSearch: onView?.search,
    pendingLayout,
    layoutPlaceholder: useLayoutPlaceholder(pendingLayout),
  }
}
